import { useState, useEffect, useContext, useMemo } from 'react';
import { 
  collection, 
  onSnapshot, 
  query, 
  where, 
  Timestamp, 
  deleteDoc, 
  doc,
  QuerySnapshot,
  DocumentData 
} from 'firebase/firestore';
import { useFirestore } from '../helpers/firebaseContext';
import { MyUserDataContext } from '../contexts';
import { Viewer, PresenceData, ProductType } from '../types';

interface PresenceMap {
  [key: string]: Viewer[]; // key is `${type}_${id}`
}

interface PresenceManagerResult {
  getViewers: (type: ProductType, id: string) => Viewer[];
  getViewerCount: (type: ProductType, id: string) => number;
  isLoading: boolean;
  totalActiveUsers: number;
}

// Singleton pattern to share the same data across all components
let sharedPresenceData: PresenceMap = {};
let sharedListeners: Set<(data: PresenceMap) => void> = new Set();
let activeListener: (() => void) | null = null;
let currentUserId: string | undefined = undefined;

export const usePresenceManager = (productType?: ProductType): PresenceManagerResult => {
  const db = useFirestore();
  const { user } = useContext(MyUserDataContext);
  const [presenceData, setPresenceData] = useState<PresenceMap>(sharedPresenceData);
  const [isLoading, setIsLoading] = useState(true);
  
  const userId = user?.uid;
  currentUserId = userId; // Update the shared user ID

  useEffect(() => {
    if (!db) {
      setIsLoading(false);
      return;
    }

    // Register this component's setState with the shared listeners
    const updateHandler = (data: PresenceMap) => setPresenceData(data);
    sharedListeners.add(updateHandler);

    // If there's no active listener yet, create one
    if (!activeListener && sharedListeners.size === 1) {
      // Create query based on productType if provided, otherwise get all
      const presenceQuery = productType
        ? query(
            collection(db, 'presence'),
            where('type', '==', productType)
          )
        : collection(db, 'presence');

      const unsubscribe = onSnapshot(
        presenceQuery,
        async (snapshot: QuerySnapshot<DocumentData>) => {
          const now = Timestamp.now();
          const newPresenceMap: PresenceMap = {};
          const expiredDocs: string[] = [];

          snapshot.docs.forEach((docSnapshot) => {
            const data = docSnapshot.data() as PresenceData;
            
            // Check if expired - be more aggressive with cleanup
            if (data.expiresAt && data.expiresAt.seconds < now.seconds) {
              expiredDocs.push(docSnapshot.id);
            //   console.log('Found expired doc:', docSnapshot.id, 'expired at:', data.expiresAt.seconds, 'now:', now.seconds);
              return; // Skip adding to map
            }

            const pageKey = data.pageKey || `${data.type}_${data.id}`;
            const viewingDuration = now.seconds - (data.timestamp?.seconds || now.seconds);
            
            const viewer: Viewer = {
              id: data.uid,
              uid: data.uid,
              name: data.name,
              email: data.email,
              timestamp: data.timestamp,
              viewingDuration: viewingDuration,
              isStale: viewingDuration > 40,
              isCurrentUser: data.uid === currentUserId,
              action: data.action || 'viewing',
            };

            if (!newPresenceMap[pageKey]) {
              newPresenceMap[pageKey] = [];
            }
            newPresenceMap[pageKey].push(viewer);
          });

          // Sort viewers for each page
          Object.keys(newPresenceMap).forEach(key => {
            newPresenceMap[key].sort((a, b) => {
              if (a.isCurrentUser) return -1;
              if (b.isCurrentUser) return 1;
              return (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0);
            });
          });

          // Update shared data and notify all listeners
          sharedPresenceData = newPresenceMap;
          sharedListeners.forEach(listener => listener(newPresenceMap));

          // Clean up expired documents in a batch
          if (expiredDocs.length > 0) {
            console.log(`Cleaning up ${expiredDocs.length} expired presence documents`);
            const deletionPromises = expiredDocs.map(docId => 
              deleteDoc(doc(db, 'presence', docId))
                .catch(error => console.log('Error cleaning expired presence:', error))
            );
            await Promise.all(deletionPromises);
          }

          setIsLoading(false);
        },
        (error) => {
          console.error('Error listening to presence:', error);
          sharedPresenceData = {};
          sharedListeners.forEach(listener => listener({}));
          setIsLoading(false);
        }
      );

      activeListener = unsubscribe;
    } else {
      setIsLoading(false);
    }

    // Cleanup
    return () => {
      sharedListeners.delete(updateHandler);
      
      // If this was the last component using presence, clean up the listener
      if (sharedListeners.size === 0 && activeListener) {
        activeListener();
        activeListener = null;
        sharedPresenceData = {};
      }
    };
  }, [db, userId, productType]);

  // Memoized helper functions
  const getViewers = useMemo(() => {
    return (type: ProductType, id: string): Viewer[] => {
      const key = `${type}_${id}`;
      return presenceData[key] || [];
    };
  }, [presenceData]);

  const getViewerCount = useMemo(() => {
    return (type: ProductType, id: string): number => {
      const key = `${type}_${id}`;
      return presenceData[key]?.length || 0;
    };
  }, [presenceData]);

  const totalActiveUsers = useMemo(() => {
    const uniqueUsers = new Set<string>();
    Object.values(presenceData).forEach(viewers => {
      viewers.forEach(viewer => uniqueUsers.add(viewer.uid));
    });
    return uniqueUsers.size;
  }, [presenceData]);

  return {
    getViewers,
    getViewerCount,
    isLoading,
    totalActiveUsers,
  };
};